import React, {useEffect, useMemo, useState} from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import {StatisticChartDef} from '../types';
import {formatAxisLabel, formatNumber, formatStatisticCell, toNumber} from '../utils/statisticValues';
import './StatisticChart.css';

interface StatisticChartProps {
    chart: StatisticChartDef;
    columns: string[];
    rows: Record<string, any>[];
    /** Pixel height, or 'fill' to take the parent's height (home-page widgets). */
    height?: number | 'fill';
}

interface Palette {
    series: string[];
    grid: string;
    axis: string;
    surface: string;
}

const readPalette = (): Palette => {
    const style = getComputedStyle(document.documentElement);
    const token = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback;
    return {
        series: Array.from({length: 8}, (_, i) => token(`--flowy-chart-${i + 1}`, '#6366f1')),
        grid: token('--flowy-line', '#e5e8ed'),
        axis: token('--flowy-muted', '#6b7688'),
        surface: token('--flowy-surface', '#ffffff'),
    };
};

/** SVG attributes cannot resolve CSS variables, so the tokens are read and re-read on theme switch. */
const usePalette = (): Palette => {
    const [palette, setPalette] = useState<Palette>(readPalette);
    useEffect(() => {
        const observer = new MutationObserver(() => setPalette(readPalette()));
        observer.observe(document.documentElement, {attributes: true, attributeFilter: ['data-theme']});
        return () => observer.disconnect();
    }, []);
    return palette;
};

const BAR_LABEL_MAX_BARS = 8;

const ChartTooltip: React.FC<any> = ({active, payload, label}) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="statistic-chart-tooltip">
            {label !== undefined && <div className="statistic-chart-tooltip-title">{formatStatisticCell(label)}</div>}
            {payload.map((item: any) => (
                <div className="statistic-chart-tooltip-row" key={item.dataKey ?? item.name}>
                    <span>
                        <i className="statistic-chart-swatch" style={{background: item.color ?? item.payload?.fill}}/>
                        {' '}{item.name}
                    </span>
                    <span>{formatNumber(toNumber(item.value))}</span>
                </div>
            ))}
        </div>
    );
};

const Legend: React.FC<{items: {name: string; color: string}[]}> = ({items}) => (
    <div className="statistic-chart-legend">
        {items.map((item) => (
            <span key={item.name}>
                <i className="statistic-chart-swatch" style={{background: item.color}}/>{item.name}
            </span>
        ))}
    </div>
);

/**
 * Draws a statistic result as a KPI number, bar, line or donut chart following
 * `chart` ({type, x, y}); x/y are result column names.
 */
export const StatisticChart: React.FC<StatisticChartProps> = ({chart, columns, rows, height = 240}) => {
    const palette = usePalette();
    const fill = height === 'fill';
    const rootClass = `statistic-chart${fill ? ' statistic-chart-fill' : ''}`;
    const frame = (chartElement: React.ReactElement) => (
        <div className="statistic-chart-area">
            <ResponsiveContainer width="100%" height={fill ? '100%' : height}>{chartElement}</ResponsiveContainer>
        </div>
    );
    const series = useMemo(() => chart.y.filter((column) => columns.includes(column)), [chart.y, columns]);
    const hasX = chart.x !== null && columns.includes(chart.x);

    // pie needs every row: what is past the limit still counts into „Ostatné“
    const drawn = useMemo(
        () => (chart.type !== 'pie' && chart.limit ? rows.slice(0, chart.limit) : rows),
        [rows, chart.type, chart.limit]
    );
    const data = useMemo(() => drawn.map((row) => {
        const point: Record<string, any> = {__x: hasX ? row[chart.x as string] : null};
        series.forEach((column) => { point[column] = toNumber(row[column]); });
        return point;
    }), [drawn, series, hasX, chart.x]);

    if (series.length === 0 || (chart.type !== 'kpi' && !hasX)) {
        return <div className="statistic-chart-empty">Graf sa nedá zobraziť — stĺpce grafu vo výsledku chýbajú.</div>;
    }
    if (rows.length === 0) {
        return <div className="statistic-chart-empty">Žiadne dáta</div>;
    }

    if (chart.type === 'kpi') {
        return (
            <div className={`statistic-kpi${fill ? ' statistic-kpi-fill' : ''}`}>
                <div className="statistic-kpi-value">{formatNumber(toNumber(rows[0][series[0]]))}</div>
                <div className="statistic-kpi-label">{series[0]}</div>
            </div>
        );
    }

    const colorOf = (index: number) => palette.series[index % palette.series.length];
    const axisTick = {fill: palette.axis, fontSize: 11};
    const legendItems = series.map((name, index) => ({name, color: colorOf(index)}));

    if (chart.type === 'pie') {
        const sorted = [...data].sort((a, b) => (b[series[0]] ?? 0) - (a[series[0]] ?? 0));
        const limit = chart.limit || sorted.length;
        const head = sorted.slice(0, limit);
        const rest = sorted.slice(limit).reduce((sum, point) => sum + (point[series[0]] ?? 0), 0);
        const slices = head.map((point) => ({name: formatAxisLabel(point.__x), value: point[series[0]] ?? 0}));
        if (rest > 0) slices.push({name: 'Ostatné', value: rest});

        return (
            <div className={rootClass}>
                {frame(
                    <PieChart>
                        <Pie data={slices} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="85%"
                             stroke={palette.surface} strokeWidth={2} isAnimationActive={false}>
                            {slices.map((slice, index) => (
                                <Cell key={slice.name} fill={slice.name === 'Ostatné' ? palette.axis : colorOf(index)}/>
                            ))}
                        </Pie>
                        <Tooltip content={<ChartTooltip/>}/>
                    </PieChart>
                )}
                <Legend items={slices.map((slice, index) => ({
                    name: slice.name,
                    color: slice.name === 'Ostatné' ? palette.axis : colorOf(index),
                }))}/>
            </div>
        );
    }

    const common = (
        <>
            <CartesianGrid vertical={false} stroke={palette.grid} strokeWidth={1}/>
            <XAxis dataKey="__x" tickFormatter={formatAxisLabel} tick={axisTick} axisLine={{stroke: palette.grid}}
                   tickLine={false} interval="preserveStartEnd" minTickGap={8}/>
            <YAxis tickFormatter={(value) => formatNumber(value, true)} tick={axisTick} axisLine={false} tickLine={false} width={48}/>
        </>
    );

    if (chart.type === 'line') {
        return (
            <div className={rootClass}>
                {frame(
                    <LineChart data={data} margin={{top: 8, right: 12, bottom: 0, left: 0}}>
                        {common}
                        <Tooltip content={<ChartTooltip/>} cursor={{stroke: palette.axis, strokeWidth: 1}}/>
                        {series.map((column, index) => (
                            <Line key={column} type="monotone" dataKey={column} name={column} stroke={colorOf(index)}
                                  strokeWidth={2} dot={data.length <= 31 ? {r: 3, strokeWidth: 0, fill: colorOf(index)} : false}
                                  activeDot={{r: 5, stroke: palette.surface, strokeWidth: 2}} isAnimationActive={false}/>
                        ))}
                    </LineChart>
                )}
                {series.length > 1 && <Legend items={legendItems}/>}
            </div>
        );
    }

    // Value labels only where they fit: small previews rely on the tooltip instead
    const showLabels = series.length === 1 && data.length <= BAR_LABEL_MAX_BARS && (fill || (height as number) >= 160);
    return (
        <div className={rootClass}>
            {frame(
                <BarChart data={data} margin={{top: showLabels ? 20 : 8, right: 12, bottom: 0, left: 0}} barGap={2} barCategoryGap="20%">
                    {common}
                    <Tooltip content={<ChartTooltip/>} cursor={{fill: palette.grid, opacity: 0.5}}/>
                    {series.map((column, index) => (
                        <Bar key={column} dataKey={column} name={column} fill={colorOf(index)} maxBarSize={24}
                             radius={[4, 4, 0, 0]} isAnimationActive={false}>
                            {showLabels && (
                                <LabelList dataKey={column} position="top" fill={palette.axis} fontSize={11}
                                           formatter={(value: any) => formatNumber(toNumber(value), true)}/>
                            )}
                        </Bar>
                    ))}
                </BarChart>
            )}
            {series.length > 1 && <Legend items={legendItems}/>}
        </div>
    );
};
